import React, { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconLock, IconCircleCheck } from "@tabler/icons-react";
import { ApiConstant, KeyConstant, LangConstant } from "const";
import { CommonUtils, StorageUtils, SystemUtils, AuthUtils } from "utils";
import { useUpdateUser, useRole } from "hooks";
import { PaperHeader, AdvancedConfirmDialog } from "components";

const ChangePasswordForm = () => {
  const { t: getLabel } = useTranslation([LangConstant.NS_COMMON, LangConstant.NS_LOGIN]);
  const { changePassword } = useUpdateUser();
  const { isServiceAdmin } = useRole();
  const inputObjErrorMsg = getLabel(LangConstant.OBJ_ERROR_MSG, { returnObjects: true });

  const [isShowSuccessMsg, setIsShowSuccessMsg] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ password: "", newPassword: "", passwordConfirmation: "" });
  const [errorMsg, setErrorMsg] = useState({ showPasswordErr: false, showPasswordConfirmationErr: false });

  const handleValidatePasswordForm = () => {
    if (passwordForm.newPassword !== passwordForm.passwordConfirmation) {
      setErrorMsg(currentState => ({ ...currentState, showPasswordConfirmationErr: true }));
      setPasswordForm(currentState => ({ ...currentState, passwordConfirmation: "" }));
      return false;
    }
    return true;
  };

  const handleSubmitPasswordForm = async event => {
    event.preventDefault();
    if (handleValidatePasswordForm()) {
      const username = StorageUtils.getStoreData(KeyConstant.KEY_USERNAME);
      const userId = StorageUtils.getStoreData(KeyConstant.KEY_USER_ID);
      const payload = { username, userId, oldPassword: passwordForm.password, newPassword: passwordForm.newPassword };
      const response = await changePassword(payload);
      if (response.status === ApiConstant.STT_OK) {
        setPasswordForm({ password: "", newPassword: "", passwordConfirmation: "" });
        setErrorMsg({ showPasswordErr: false, showPasswordConfirmationErr: false });
        setIsShowSuccessMsg(true);
      } else if (response.status === ApiConstant.STT_FORBIDDEN) {
        setPasswordForm(currentForm => ({ ...currentForm, password: "" }));
        setErrorMsg(currentState => ({ ...currentState, showPasswordErr: true }));
      } else {
        SystemUtils.appendNotification(getLabel(LangConstant.TXT_UPDATE_ERROR), "error");
      }
    }
  };

  const handleChangePasswordForm = event => {
    const id = event.target.id;
    const value = event.target.value;
    setPasswordForm(currentState => ({ ...currentState, [id]: value }));
  };

  const handleFocusPasswordInput = () => {
    setErrorMsg(currentState => ({ ...currentState, showPasswordErr: false }));
  };

  const handleFocusPasswordConfirmationInput = () => {
    setErrorMsg(currentState => ({ ...currentState, showPasswordConfirmationErr: false }));
  };

  const isDisabled = !(
    passwordForm.password &&
    (isServiceAdmin ? passwordForm.newPassword.length > 5 : passwordForm.newPassword.length) &&
    passwordForm.passwordConfirmation
  );

  return (
    <div>
      <div>
        {!isServiceAdmin && (
          <PaperHeader
            icon={<IconLock size={24} className="text-primary" />}
            title={getLabel(CommonUtils.getNSKey(LangConstant.NS_LOGIN, LangConstant.L_PASSWORD))}
            rightEl={
              isShowSuccessMsg ? (
                <div className="d-flex align-items-center">
                  <IconCircleCheck size={20} className="text-success me-2" />
                  <span className="text-secondary">{getLabel(LangConstant.TXT_UPDATE_PASSWORD_SUCCESS)}</span>
                </div>
              ) : null
            }
          />
        )}
        <form onSubmit={handleSubmitPasswordForm} className="mt-3">
          <div className="mb-3">
            <label className="form-label">{getLabel(LangConstant.L_OLD_PASSWORD)}</label>
            <input
              id="password"
              type="password"
              autoComplete="on"
              className={`form-control ${errorMsg.showPasswordErr ? "is-invalid" : ""}`}
              value={passwordForm.password}
              onChange={handleChangePasswordForm}
              onFocus={handleFocusPasswordInput}
              placeholder={
                errorMsg.showPasswordErr ? inputObjErrorMsg.wrongPassword : getLabel(LangConstant.P_OLD_PASSWORD)
              }
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              {getLabel(CommonUtils.getNSKey(LangConstant.NS_LOGIN, LangConstant.L_NEW_PASSWORD))}
            </label>
            <input
              id="newPassword"
              type="password"
              autoComplete="on"
              className="form-control"
              value={passwordForm.newPassword}
              onChange={handleChangePasswordForm}
              placeholder={getLabel(CommonUtils.getNSKey(LangConstant.NS_LOGIN, LangConstant.P_PASSWORD))}
              maxLength={isServiceAdmin ? 32 : undefined}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              {getLabel(CommonUtils.getNSKey(LangConstant.NS_LOGIN, LangConstant.L_NEW_PASSWORD_CONFIRMATION))}
            </label>
            <input
              id="passwordConfirmation"
              type="password"
              autoComplete="on"
              className={`form-control ${errorMsg.showPasswordConfirmationErr ? "is-invalid" : ""}`}
              value={passwordForm.passwordConfirmation}
              onChange={handleChangePasswordForm}
              onFocus={handleFocusPasswordConfirmationInput}
              placeholder={
                errorMsg.showPasswordConfirmationErr
                  ? inputObjErrorMsg.passwordConfirmationNotMatch
                  : getLabel(CommonUtils.getNSKey(LangConstant.NS_LOGIN, LangConstant.P_PASSWORD))
              }
            />
          </div>
          <div className="text-center">
            <button
              className="btn btn-primary"
              type="submit"
              disabled={isDisabled}
              style={{ minWidth: "345px", minHeight: "46px" }}
            >
              {getLabel(LangConstant.L_UPDATE_PASSWORD_BUTTON)}
            </button>
          </div>
        </form>

        {isServiceAdmin && (
          <AdvancedConfirmDialog
            open={isShowSuccessMsg}
            title={getLabel("TXT_PASSWORD_CHANGE_SUCCESS_TITLE")}
            content={getLabel("TXT_PASSWORD_CHANGE_SUCCESS_TEXT")}
            submitProps={{
              submitText: getLabel("L_UNDERSTOOD"),
              onClick: AuthUtils.handleLogout,
            }}
            cancelProps={{
              style: { display: "none" },
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ChangePasswordForm;
